import { HeaderAction } from "@circle-react/components/QuickPostV3/Content/HeaderAction";
import { useRequestClose } from "@circle-react/components/QuickPostV3/contexts/requestCloseContext";
import { useComposerLayout } from "@circle-react/components/QuickPostV3/hooks/useComposerLayout";
import { useComposerSpace } from "@circle-react/components/QuickPostV3/hooks/useComposerSpace";
import { useCoverPicker } from "@circle-react/components/QuickPostV3/hooks/useCoverPicker";
import { useQuickPostV3ModalStore } from "@circle-react/components/QuickPostV3/store";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";

interface HeaderProps {
  variant: "create" | "edit";
}

export const Header = ({ variant }: HeaderProps) => {
  const isFullscreen = useQuickPostV3ModalStore(state => state.isFullscreen);
  const goToSettings = useQuickPostV3ModalStore(state => state.goToSettings);
  const toggleFullscreen = useQuickPostV3ModalStore(
    state => state.toggleFullscreen,
  );
  const { canManageSelectedSpace, shouldShowAddCover } = useComposerSpace();
  const { openCoverPicker } = useCoverPicker();
  const { isSmScreen } = useComposerLayout();
  const onRequestClose = useRequestClose();
  const { isElectron } = useElectronAPI();
  const fullscreenLabel = isFullscreen
    ? t("builder_ui.canvas.exit_fullscreen")
    : t("quick_post_v2.header.fullscreen");
  const addCoverLabel = t("quick_post_v2.header.add_cover");
  const closeLabel = t("quick_post_v2.header.close");
  const settingsLabel = t("quick_post_v2.header.settings");
  const title = t(
    variant === "edit"
      ? "quick_post_v3.edit_title"
      : "quick_post_v3.create_title",
  );

  return (
    <header
      className={classNames("flex shrink-0 flex-col px-6 pb-0 pt-6", {
        "mt-6": isElectron && isFullscreen,
      })}
      data-testid="quick-post-v3-header"
    >
      <div className="mb-3 flex w-full flex-row items-center gap-4">
        <Typography
          className="min-w-0 flex-1 truncate"
          color="primary"
          variant="heading-lg"
        >
          {title}
        </Typography>
        <ul className="flex shrink-0 list-none items-center gap-2">
          {canManageSelectedSpace && (
            <HeaderAction
              kind="icon"
              label={settingsLabel}
              icon="settings-gear"
              onClick={goToSettings}
            />
          )}
          {shouldShowAddCover && (
            <HeaderAction
              kind="text"
              label={addCoverLabel}
              startIcon="image"
              onClick={openCoverPicker}
            />
          )}
          {!isSmScreen && (
            <HeaderAction
              kind="icon"
              label={fullscreenLabel}
              icon={isFullscreen ? "minimize" : "expand"}
              onClick={toggleFullscreen}
            />
          )}
          <HeaderAction
            kind="icon"
            label={closeLabel}
            icon="cross"
            onClick={onRequestClose}
          />
        </ul>
      </div>
      <div aria-hidden className="border-primary border-t" />
    </header>
  );
};
